import React from "react";

import { initializeApp } from 'firebase/app';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { getAnalytics } from "firebase/analytics";
import { EmailAuthProvider, GoogleAuthProvider } from "firebase/auth";
import {
    buildCollection,
    FirebaseCMSApp,
    Navigation,
    NavigationBuilder,
    NavigationBuilderProps
} from "@camberi/firecms";

import { firebaseConfig, firebaseConfigSandbox } from "../firebase_config";
// import { ExampleCMSView } from "./ExampleCMSView";
import logo from "./images/remotecoach-logo.jpg";
import { textSearchController } from "./text_search";
// import {
//     localeSchema,
//     productAdditionalColumn, productCallbacks,
//     productExtraActionBuilder,
//     productSchema
// } from "./schemas/products_schema";

import { usersSchema, attendeesSchema, purchasedOneToOneSessionSchema, purchasedOneToOneSessionCallbacks  } from "./schemas/users_schema";
import { deviceSchema } from "./schemas/device_schema";
import { fitnessLevelSchema } from "./schemas/fitness_level_schema";
import { onboardingSchema } from "./schemas/onboarding_schema";
import { videoCategoriesSchema } from "./schemas/video_categories_schema";
import { splashScreenSchema } from "./schemas/splash_screen_schema";
import { communitySchema, commentSchema, replySchema } from "./schemas/community_schema";
import { productSchema, priceSchema } from "./schemas/product_schema";
import { customThemeColorSchema, customThemeStringsSchema } from "./schemas/custom_theme_schema";
import {
    blogSchema
} from "./schemas/blog_schema";
import { ecommerceProduct, sampleAdditionalExportColumn } from "./schemas/ecommerce_product";
import {
    recipeSchema,
} from "./schemas/recipe_schema";
import {
    videoSchema,
} from "./schemas/video_schema";
import {
    coachesSchema,
} from "./schemas/coaches_schema";
import {
    workoutCategoriesSchema,
} from "./schemas/workout_categories_schema";
import {
    sessionsSchema,
} from "./schemas/sessions_schema";
// import {
//     upcomingSessionsSchema,
// } from "./schemas/upcoming_sessions_schema";
import {
    subscriptionsSchema,
} from "./schemas/subscriptions_schema";
import {
    paymentsSchema,
} from "./schemas/payments_schema";
import {
    checkoutSessionsSchema,
} from "./schemas/checkout_sessions_schema";
import {
    staticPagesSchema,
} from "./schemas/static_pages_schema";
import {
    subscriptionTiersSchema,
} from "./schemas/subscription_tiers_schema";
import {
    linksSchema,
} from "./schemas/links_schema";
import {
    shoppingSchema,
} from "./schemas/shopping_schema";
import { questionsSchema, answerSchema } from "./schemas/questions_schema";
import { generalSettingSchema } from "./schemas/general_settings";
import { pushNotificationsSchema } from "./schemas/push_notifications";
import { notificationTypesSchema } from "./schemas/notification_types";
import { customSchemaOverrideHandler } from "./schemas/custom_schema_resolver";
import {
    mediaSchema,
} from "./schemas/media_schema";
import "typeface-rubik";
import "typeface-space-mono";
// import { Locale, Product } from "./types";
import {
    inAppProductsSchema,
} from "./schemas/in_app_products_schema";
import { feedbackQuestionsSchema, feedbackAnswerSchema } from "./schemas/feedback_questions_schema";
import { oneToOneProductSchema, oneToOnePriceSchema } from "./schemas/one_to_one_product_schema";
// import { programSchema, programWeekSchema, programDaySchema, programDayCategorySchema, programVideoSchema, } from "./schemas/program_schema";

function SampleApp() {
    // const productsCollection = buildCollection<Product>({
    //     path: "products",
    //     schema: productSchema,
    //     callbacks: productCallbacks,
    //     name: "Products",
    //     group: "Main",
    //     description: "List of the products currently sold in our shop",
    //     textSearchEnabled: true,
    //     additionalColumns: [productAdditionalColumn],
    //     filterCombinations: [{ category: "desc", available: "desc" }],
    //     permissions: ({ authController }) => ({
    //         edit: true,
    //         create: true,
    //         // we use some custom logic by storing user data in the `extra`
    //         // field of the user
    //         delete: authController.extra?.roles.includes("admin")
    //     }),
    //     extraActions: productExtraActionBuilder,
    //     subcollections: [localeCollection],
    //     excludedProperties: ["images"]
    // });

    const usersCollection = buildCollection({
        path: "users",
        schema: usersSchema,
        name: "Users",
        defaultSize: "xs",
        group: "Main",
        description: "Registered users",
        textSearchEnabled: true,
        additionalColumns: [
            // {
            //     id: "sample_additional",
            //     title: "Sample additional",
            //     builder: () => "Content of a generated column"
            // },
            // isSubscribedAdditionalColumn
        ],
        properties: ["firstName", "lastName", "email", "isCoach", "isSubscribed", "currentHeight", "heightUnit", "timezone", "currentWeight", "weightUnit",
            "photoURL", "userGroup", "createdAt"],
        subcollections: [{
            name: "One to one credits",
            schema: purchasedOneToOneSessionSchema,
            path: "purchased_one_to_one_session",
            properties: ["quantity", "message", "currency", "rc_fee", "createdByUser", "createdAt"],
            permissions: {
                edit: false,
                delete: false
            },
            callbacks: purchasedOneToOneSessionCallbacks,
            initialSort: ["createdAt", "desc"]
        } ,{
            name: "Subscriptions",
            schema: subscriptionsSchema,
            path: "subscriptions",
            properties: ["status", "items", "stripeLink", "current_period_start", "current_period_end", "created"],
        }, {
            name: "Payments",
            schema: paymentsSchema,
            path: "payments",
            properties: ["status", "description", "amount", "currency", "created"],
        }, {
            name: "Checkout Sessions",
            schema: checkoutSessionsSchema,
            path: "checkout_sessions",
            properties: ["client", "mode", "created", "cancel_url", "price", "success_url", "sessionId", "url"],
        }],
        initialSort: ["createdAt", "desc"]
    });

    const blogCollection = buildCollection({
        path: "blogs",
        schema: blogSchema,
        name: "Wellness",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["name", "header", "status", "order", "content", "isFree", "reviewed", "publishedAt", "createdAt"],
        // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
        description: "Collection of articles and posts",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"]
    });

    const ecommerceProductCollection = buildCollection({
        path: "ecommerce_products",
        schema: ecommerceProduct,
        name: "Ecommerce Product",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["name", "header", "status", "order", "content", "isFree", "productUrl", "currency", "price", "reviewed", "publishedAt", "createdAt"],
        description: "Collection of articles and posts",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"]
    });

    const oneToOneProductCollection = buildCollection({
        path: "one_to_one_products",
        schema: oneToOneProductSchema,
        name: "One To One Products",
        group: "Main",
        defaultSize: "l",
        properties: ["name", "active"],
        description: "Collection of prices",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        permissions: {
            // create: false,
            delete: false
        },
         subcollections: [
            {
                name: "Prices",
                schema: oneToOnePriceSchema,
                path: "prices",
                properties: ["stripe_metadata_nickName", "product", "name", "content", "currency", "unit_amount", "number_of_sessions", "active", "showDefault"],
                permissions: {
                    // create: false,
                    delete: false
                },
            },
        ]
    });

    const splashScreenCollection = buildCollection({
        path: "splash_screen",
        schema: splashScreenSchema,
        name: "Splash Screen",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["title", "image", "order", "description", "category","createdAt"],
        textSearchEnabled: false,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"]
    });

    const communityCollection = buildCollection({
        path: "community",
        schema: communitySchema,
        name: "Community",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["description", "isDeleted", "createdAt", "commentsCount", "likeCount", "media", "isPublished"],
        textSearchEnabled: false,
        initialFilter: {
            // "status": ["==", "published"]
        },
        subcollections: [
            {
                name: "Comment",
                schema: commentSchema,
                path: "comments",
                properties: ["comment", "commentsCount", "createdAt", "isDeleted", "likeCount", "reactionCount", "isPublished"],
                permissions: {
                    create: false,
                    delete: false
                },
                subcollections: [
                    {
                        name: "Reply",
                        schema: replySchema,
                        path: "replies",
                        properties: ["comment", "commentsCount", "createdAt", "isDeleted", "likeCount", "reactionCount", "isPublished"],
                        permissions: {
                            create: false,
                            delete: false
                        },
                    },
                ],
            },
            
        ],
        initialSort: ["createdAt", "desc"],
        permissions: {
            create: false,
            delete: false
        },
    });

    const generalSettingCollection = buildCollection({
        path: "general_settings",
        schema: generalSettingSchema,
        name: "General Setting",
        group: "Main",
        defaultSize: "l",
        properties: ["cookie_privacy", "payment_price_unit", "profile_placeholder", "content", "support_email", "terms_and_cond_url", "play_store_link", "apple_store_link", 
            "login_image", "copyright_text", "referral_points_value", "enable_re_engagement", "first_engagement_email", "second_engagement_email", "monthly_discount_percentage", "quarterly_discount_percentage", "feedback_interval",
            "one_to_one_fee_enable", "one_to_one_created_id", "one_to_one_customer_id", "refer", "referral_point_enable", "referral_point_value", "referral_point_discount", "send_session_push_notification", "send_community_push_notification",
            "remaining_one_one_credits",
        ],
        description: "Collection of articles and posts",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        permissions: {
            create: false
        },
    });

    const customThemeColorCollection = buildCollection({
        path: "custom_color",
        schema: customThemeColorSchema,
        name: "Colors",
        group: "Main",
        defaultSize: "l",
        properties: ["background", "error", "hintColor", "indicatorColor", "inversePrimary", "inverseSurface", "onBackground", "onError", "onPrimary", "onPrimaryContainer", "onSecondary", "onSecondaryContainer", "onSurface",
                        "onSurfaceVariant", "onTertiary", "onTertiaryContainer", "outline", "primary", "primaryContainer", "scrollbarColor", "secondary", "secondaryContainer", "shadow", "surface", "surfaceVariant", "tertiary", "tertiaryContainer"],
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        permissions: {
            create: false
        }
    });

    const customThemeStringCollection = buildCollection({
        path: "custom_string",
        schema: customThemeStringsSchema,
        name: "Strings",
        group: "Main",
        defaultSize: "l",
        properties: ["appName","appLogo", "appId",  "referral_point_enable", "referral_point", "referral_point_discount", "referral_discount_amount", "referral_point_enable", "clearcacheUrl", "ownerEmail", "showSignup", "singup_status_on_mobile", "disable_google", "enable_referral", "hasOneToOne", "coachName", "copyRight", "fallbackUrl",
         "networkImagePlaceholder", "webUrl", "showQA", "isGreg", "showLoginVideo", "app_store_link", 
         "defaultImage", "nutrition_title", "showNutritionFilter", "playstore_link", "splash_initial_text", "transformation_title", 
         "login_over_image_text", "login_screen_text","show_home", "show_blog", "show_community", "show_recipe", "inapp_purchase", "show_library", "show_library_category", "isMacroHide", "show_shop"],
        // textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        permissions: {
            create: false
        }
    });

    const productCollection = buildCollection({
        path: "products",
        schema: productSchema,
        name: "Product",
        group: "Main",
        defaultSize: "l",
        properties: ["name", "active"],
        description: "Collection of prices",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        permissions: {
            create: false,
            delete: false
        },
         subcollections: [
            {
                name: "Prices",
                schema: priceSchema,
                path: "prices",
                properties: ["stripe_metadata_nickName", "product", "name", "content", "currency", "unit_amount", "active", "showDefault"],
                permissions: {
                    create: false,
                    delete: false
                },
            },
        ]
    });

    const recipeCollection = buildCollection({
        path: "recipes",
        schema: recipeSchema,
        name: "Nutrition",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["name", "header", "mealType", "order", "tags", "calories", "carbohydrate", "fat", "protein", "serves", "ingredients", "directions",
         "status", "content", "reviewed", "publishDate", "isFree", "createdAt"],
        // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
        description: "Collection of recipes",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"]
    });

    const videoCollection = buildCollection({
        path: "video_uploads",
        schema: videoSchema,
        name: "Videos",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "l",
        properties: ["videoName", "description", "categories", "timer", "order", "thumbnail", "videoUrl", "isFree", "status", "publishDate", "createdAt"],
        description: "Collection of free videos",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"],
    });

    const workoutCategoriesCollection = buildCollection({
        path: "workout_categories",
        schema: workoutCategoriesSchema,
        name: "Workout Categories",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "s",
        properties: ["categoryName", "description", "thumbnail","order", "tags", "createdAt", "status", "createAt"],
        description: "List of workout categories",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    const videoCategoriesCollection = buildCollection({
        path: "video_categories",
        schema: videoCategoriesSchema,
        name: "Video Categories",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "s",
        properties: ["categoryName", "parent", "description", "order", "thumbnail", "status", "createdAt"],
        // subcollections: [
        //     {
        //         name: "Sub Category",
        //         schema: videoSubCategoriesSchema,
        //         path: "video_sub_categories",
        //         properties: ["categoryName", "description", "order", "thumbnail", "status", "createdAt"],
        //         subcollections: [
        //             {
        //                 name: "Videos",
        //                 schema: videoSchema,
        //                 path: "videos",
        //                 properties: ["videoName", "thumbnail", "timer", "description", "order", "videoUrl", "tags", "isFree", "publishDate", "status"]
        //             }
        //         ]
        //     },
        //     {
        //         name: "Videos",
        //         schema: videoSchema,
        //         path: "videos",
        //         properties: ["videoName", "thumbnail", "timer", "description", "order", "videoUrl", "tags", "isFree", "publishDate", "status"]
        //     },
        // ],
        description: "List of Video categories",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

   

    const onboardingCollection = buildCollection({
        path: "onboarding",
        schema: onboardingSchema,
        name: "Onboarding",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "s",
        properties: ["header", "subHeader", "imageRef", "backgroundImage", "order", "status", "createdAt"],
        description: "List of Onboarding categories",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    const coachesCollection = buildCollection({
        path: "coaches",
        schema: coachesSchema,
        name: "Coaches",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["firstName", "lastName", "canCreateToken", "coachLevel", "isApproved", "createdAt"],
        // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
        description: "List of coach in the system",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"],
    });

    const sessionsCollection = buildCollection({
        path: "sessions",
        schema: sessionsSchema,
        name: "Sessions",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["sessionName", "coachReference", "description", "startDateTimeStamp", "startDate", "expectedDurationInMinutes", "workoutCategoryReference", "isFree", "clientReference", "channelName", "category", "coach", "savedVideoUrl", "externalLiveUrl", "startedAt", "endedAt", "isArchived", "status", "sessionType"],
        description: "List of sessions",
        textSearchEnabled: true,
        initialSort: ["startDate", "desc"],
        permissions: ({ authController }) => ({
            // create: false,
        }),
        subcollections: [{
            name: "Users",
            schema: attendeesSchema,
            path: "attendees",
            properties: ["name", "hr", "calorie", "createdAt"],
            defaultSize: "xs",
            permissions: ({ authController }) => ({
                create: false,
            }),
        }],
    });

    // const upcomingSessionsCollection = buildCollection({
    //     path: "sessions",
    //     schema: upcomingSessionsSchema,
    //     name: "Upcoming Sessions",
    //     group: "Main",
    //     exportable: {
    //         additionalColumns: [sampleAdditionalExportColumn]
    //     },
    //     defaultSize: "xs",
    //     properties: ["coach","sessionName", "description", "category", "channelName", "savedVideoUrl", "startDate", "startedAt", "endedAt", "isArchived","status"],
    //     // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
    //     description: "Upcoming sessions list",
    //     textSearchEnabled: true,
    //     initialFilter: {
    //         "startDate": ["!=", null],
    //         "isArchived": ["==", false],
    //         "status": ["in", ['waiting', 'open', 'live']],
    //     },
    //     filterCombinations: [{ startDate: "asc" }],
    // });

    // const pastSessionsCollection = buildCollection({
    //     path: "sessions",
    //     schema: sessionsSchema,
    //     name: "Past Sessions",
    //     group: "Main",
    //     exportable: {
    //         additionalColumns: [sampleAdditionalExportColumn]
    //     },
    //     defaultSize: "xs",
    //     properties: ["coach","sessionName", "description", "category", "channelName", "savedVideoUrl", "startDate", "startedAt", "endedAt", "isArchived","status"],
    //     // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
    //     description: "Past sessions list",
    //     textSearchEnabled: true,
    //     initialFilter: {
    //         "savedVideo": ["==", true],
    //         "isArchived": ["==", false],
    //         "status": ["in", ['finished', 'closed']],
    //     },
    //     filterCombinations: [{ startDate: "desc" }],
    // });

    const staticPagesCollection = buildCollection({
        path: "static_pages",
        schema: staticPagesSchema,
        name: "Static Pages",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "m",
        properties: ["title", "description", "visibility", "status", "createdAt"],
        // description: "Collection of blog entries included in our [awesome blog](https://www.google.com)",
        description: "List of Static Pages",
        textSearchEnabled: true,
        initialFilter: {
            // "status": ["==", "published"]
        },
        initialSort: ["createdAt", "desc"],
    });

    const subscriptionTiersCollection = buildCollection({
        path: "subscription_tiers",
        schema: subscriptionTiersSchema,
        name: "Subscription Tiers",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["name", "range_from", "range_to", "fee_amount"],
        description: "List of subscription tiers",
        textSearchEnabled: false,
        permissions: ({ authController }) => {
            // const isAdmin = authController.extra?.roles.includes("admin");
            return ({
                edit: false,
                create: false,
                delete: false
            })
        },
    });

    const linksCollection = buildCollection({
        path: "links",
        schema: linksSchema,
        name: "Links",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "s",
        properties: ["title", "url", "thumbnail", "code", "sort", "type", "status", "createdAt"],
        description: "List of links",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    const questionsCollection = buildCollection({
        path: "questions",
        schema: questionsSchema,
        name: "Questions",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["question", "answer", "order", "published", "createdAt"],
        subcollections: [{
            name: "Answer",
            schema: answerSchema,
            path: "answers",
            properties: ["answer", "order", "status", "createdAt"]
        }],
        description: "List of Questions",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    const mediaCollection = buildCollection({
        path: "media",
        schema: mediaSchema,
        name: "Media",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["name","mediaUrl", "createdAt"],
        description: "List Of Media",
        textSearchEnabled: true,
        initialSort: ["createdAt","desc"],
    });

    const deviceCollection = buildCollection({
        path: "devices",
        schema: deviceSchema,
        name: "Devices",
        group: "Main",
        defaultSize: "xs",
        properties: ["name", "icon", "status", "createdAt", "updatedAt"],
        description: "Collection of devices",
        textSearchEnabled: false,
        initialSort: ["createdAt","desc"],
    });

    const fitnessLevelCollection = buildCollection({
        path: "fitnessLevel",
        schema: fitnessLevelSchema,
        name: "Fitness Level",
        group: "Main",
        defaultSize: "l",
        properties: ["title", "fitnessLevelName", "description", "status", "order", "createdAt", "UpdatedAt"],
        description: "Collection of devices",
        textSearchEnabled: false,
        initialSort: ["createdAt","desc"],
    });
    
    const pushNotificationsCollection = buildCollection({
        path: "push_notifications",
        schema: pushNotificationsSchema,
        name: "Push Notification",
        group: "Main",
        defaultSize: "m",
        properties: ["title", "body", "event", "users", "createdAt"],
        description: "Collection of push notification",
        textSearchEnabled: false,
        initialSort: ["createdAt","desc"],
        permissions: {
            edit: false
        },
    });

    const notificationTypesCollection = buildCollection({
        path: "notifications_types",
        schema: notificationTypesSchema,
        name: "Notification Types",
        group: "Main",
        defaultSize: "s",
        properties: ["title", "description", "key"],
        description: "Collection of notification types",
        permissions: {
            // edit: false,
            delete: false,
            create: false,
        },
    });


    const inAppProductsCollection = buildCollection({
        path: "in_app_products",
        schema: inAppProductsSchema,
        name: "In App Products",
        group: "Main",
        defaultSize: "s",
        properties: ["title", "sub_description", "description", "productId", "currency", "price", "thumbnail"],
        description: "Collection of in app products",
    });

    const feedbackQuestionsCollection = buildCollection({
        path: "feedback_questions",
        schema: feedbackQuestionsSchema,
        name: "Feedback Questions",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["question", "category", "order", "published", "createdAt", "answer"],
        subcollections: [{
            name: "Feedback Answer",
            schema: feedbackAnswerSchema,
            path: "feedback_answers",
            properties: ["answer", "order", "status", "createdAt"]
        }],
        description: "List of Feedback Questions",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    const shoppingCollection = buildCollection({
        path: "shopping",
        schema: shoppingSchema,
        name: "Shopping",
        group: "Main",
        exportable: {
            additionalColumns: [sampleAdditionalExportColumn]
        },
        defaultSize: "xs",
        properties: ["title", "description", "order", "image", "link_button_text", "link_url", "createdAt"],
        description: "List of Shopping",
        textSearchEnabled: true,
        initialSort: ["createdAt", "desc"],
    });

    // const programsCollection = buildCollection({
    //     path: "programs",
    //     schema: programSchema,
    //     name: "Programs",
    //     defaultSize: "xs",
    //     group: "Main",
    //     description: "List of programs",
    //     textSearchEnabled: true,
    //     properties: ["title", "description", "thumbnail", "image", "number_of_weeks", "order", "status", "createdAt"],
    //     subcollections: [{
    //         name: "Weeks",
    //         schema: programWeekSchema,
    //         path: "weeks",
    //         properties: ["name", "number_of_days", "order", "status", "createdAt"],
    //         permissions: {
    //             create: false,
    //             delete: false
    //         },
    //         subcollections: [{
    //             name: "Days",
    //             schema: programDaySchema,
    //             path: "days",
    //             properties: ["name", "order", "status", "createdAt"],
    //             permissions: {
    //                 create: false,
    //                 delete: false
    //             },
    //             subcollections: [{
    //                 name: "Categories",
    //                 schema: programDayCategorySchema,
    //                 path: "categories",
    //                 properties: ["name", "sets", "order", "status", "createdAt"],
    //                 initialSort: ["order", "asc"],
    //                 subcollections: [{
    //                     name: "Videos",
    //                     schema: programVideoSchema,
    //                     path: "videos",
    //                     properties: ["name", "type", "video", "repetations", "timer", "order", "status", "enableWeight", "createdAt"],
    //                     initialSort: ["order", "asc"]
    //                 }],
    //             }],
    //             initialSort: ["order", "asc"]
    //         }],
    //         initialSort: ["order", "asc"]
    //     }],
    //     initialSort: ["createdAt", "desc"]
    // });

    // const githubLink = (
    //     <Tooltip
    //         title="See this project on GitHub. This button is only present in this demo">
    //         <IconButton
    //             href={"https://github.com/Camberi/firecms"}
    //             rel="noopener noreferrer"
    //             target="_blank"
    //             component={"a"}
    //             size="large">
    //             <GitHub/>
    //         </IconButton>
    //     </Tooltip>
    // );

    // const customViews: CMSView[] = [{
    //     path: ["additional", "additional/:id"],
    //     name: "More Features",
    //     group: "Content",
    //     description: "Additional Views coming soon...",
    //     view: <ExampleCMSView/>
    // }];


    const onFirebaseInit = (config: Object) => {
        // Just calling analytics enables screen tracking
        getAnalytics();
    };

    // const myAuthenticator: Authenticator = async ({ user, authController }) => {
    //     // This is an example of retrieving async data related to the user
    //     // and storing it in the user extra field
    //     const sampleUserData = await Promise.resolve({
    //         roles: ["admin"]
    //     });
    //     authController.setExtra(sampleUserData);
    //     console.log("Allowing access to", user?.email);
    //     return true;
    // };

    const navigation: NavigationBuilder = async ({
        user,
        authController
    }: NavigationBuilderProps) => {
        const navigation: Navigation = {
            collections: [
                usersCollection,
                recipeCollection,
                blogCollection,
                workoutCategoriesCollection,
                videoCategoriesCollection,
                coachesCollection,
                sessionsCollection,
                staticPagesCollection,
                linksCollection,
                questionsCollection,
                onboardingCollection,
                mediaCollection,
                deviceCollection,
                fitnessLevelCollection,
                videoCollection,
                generalSettingCollection,
                splashScreenCollection,
                productCollection,
                customThemeColorCollection,
                customThemeStringCollection,
                pushNotificationsCollection,
                communityCollection,
                notificationTypesCollection,
                inAppProductsCollection,
                ecommerceProductCollection,
                feedbackQuestionsCollection,
                oneToOneProductCollection,
                shoppingCollection,
                // programsCollection
            ]
        };
        console.log("Condition");
        console.log(window.location.href.includes('sandbox') || window.location.href.includes('test') || window.location.href.includes('localhost'));
        const app = initializeApp((window.location.href.includes('sandbox') || window.location.href.includes('test') || window.location.href.includes('localhost')) ? firebaseConfigSandbox : firebaseConfig);
        const db = getFirestore(app);
        const docRef = doc(db, "coaches", user!.uid);
        const docSnap = await getDoc(docRef);
        // let collections:EntityCollection[] = [];

        if (!docSnap.exists() || docSnap.data().isApproved === false) {
            authController.signOut();
        } else {
            if (docSnap.data().isAdmin) {
                const addRole = await Promise.resolve({
                    roles: ["admin"]
                });
                authController.setExtra(addRole);
                navigation.collections.push(buildCollection(subscriptionTiersCollection));
            }
        }
        return navigation;
    };

    return <FirebaseCMSApp
        name={"Lewis Bloor - CMS"}
        // authentication={myAuthenticator}
        signInOptions={[
            GoogleAuthProvider.PROVIDER_ID,
            EmailAuthProvider.PROVIDER_ID
        ]}
        textSearchController={textSearchController}
        // allowSkipLogin={true}
        logo={logo}
        navigation={navigation}
        schemaOverrideHandler={customSchemaOverrideHandler}
        firebaseConfig={(window.location.href.includes('sandbox') || window.location.href.includes('test') || window.location.href.includes('localhost')) ? firebaseConfigSandbox : firebaseConfig}
        onFirebaseInit={onFirebaseInit}
    // toolbarExtraWidget={githubLink}
    />;
}

export default SampleApp;
